import type { DocNode } from '@atlaskit/adf-schema';

import type { ConfluencePageInitCommandProperties } from '@atlassian/rovo-content-bridge-api-commands';
import { ADFStreamer } from '@atlassian/ai-model-io/adf-streamer';
import { CommandHandler, type PassThroughCommandProps } from '@atlassian/editor-rovo-bridge';

import { fg } from '@confluence/feature-gating';

import { log } from '../utils/aifc-logging';
import { chunkSubstr, getDocument, isEmptyDocument } from '../utils/bridge-document-utils';
import type { ExtraCommandProps } from '../RovoBridgeCommandListener';

export class PageInitCommandHandler extends CommandHandler<
	ConfluencePageInitCommandProperties,
	void
> {
	constructor(
		props: PassThroughCommandProps,
		private extraProps: Pick<ExtraCommandProps, 'reuploadImages'>,
	) {
		super(props);
	}

	async execute(payload: ConfluencePageInitCommandProperties): Promise<void> {
		log('handle confluence-page-init-command');
		const document = await getDocument(this.props.editorAPI);
		if (!document) {
			log('PageInitCommand received but document is not available');
			return;
		}

		const isEmpty = isEmptyDocument(document as DocNode);

		log(`Received ConfluencePageInitCommand with isEmpty: ${isEmpty}`, payload?.data);

		if (!isEmpty) {
			return;
		}

		const editorSchema = this.props.editorAPI.core.sharedState.currentState()?.schema;
		if (!editorSchema) {
			log('Editor schema is not available');
			return;
		}

		log('Streaming initial ADF from ConfluencePageInitCommand', payload?.data.adf);
		this.props.onStreamStart?.({ streamType: 'init', payload });
		this.props.onStream?.({ streamType: 'init', payload });
		// In case ADF is invalid, we chunk it to retain as much of the ADF as possible
		const streamer = new ADFStreamer(editorSchema);
		const adfChunks = chunkSubstr(payload.data.adf, 50);
		for (const chunk of adfChunks) {
			log('Processing chunk from ConfluencePageInitCommand', chunk);
			streamer.processChunk(chunk);
		}
		const adfDoc = streamer.getLastDoc();
		log('Replacing document with ADF from ConfluencePageInitCommand', adfDoc);
		log('Replace document function - ', this.props.editorAPI.core.actions.replaceDocument);
		this.props.editorAPI.core.actions.replaceDocument(adfDoc, { scrollIntoView: false });

		// Trigger image re-uploading if handler is available
		if (this.extraProps.reuploadImages && adfDoc) {
			if (fg('platform_editor_aifc_editor_rovo_bridge_batch_1')) {
				await this.extraProps.reuploadImages(adfDoc);
			} else {
				void this.extraProps.reuploadImages(adfDoc);
			}
		}

		this.props.onStreamEnd?.({ streamType: 'init', payload });
	}
}
